import React from 'react';
import { useTranslation } from 'react-i18next';
import Heading from '../../../../generic-components/Heading';
import { ReactComponent as IconAvailableBalance } from '../../../../assets/dashboard/available-balance-icon.svg';
import { BucketBalance } from '../../components/BucketBalance';
import { useStore } from '../../../../store';
import { getSubAccountResponse, getTotalCheckingBalance, getUserBalances } from '../../utils/balance.utils';
import { BalanceView } from '../../components/BalanceView';
import { showEmergencyBucketBalance, showTaxBucketBalance } from '../../utils/widget.utils';
import { VIEWS_MAP } from '../../../../router/constants';
import { SubAccountType } from '../../enums';
import { SimpleWidgetSkeleton } from '../../components/skeletons/SimpleWidgetSkeleton';

const testIdPrefix = 'checkingAccountWidget';

export const CheckingAccountWidget = ({ isLoading }: { isLoading: boolean }) => {
  const getSummaryData = useStore(state => state.getSummaryData);
  const userBalances = getUserBalances(getSummaryData());
  const totalBalance = getTotalCheckingBalance(userBalances);
  const accountBalance = userBalances?.balance;
  const taxBucket = getSubAccountResponse(userBalances, SubAccountType.taxBucket);
  const taxBucketBalance = taxBucket?.balance || 0;
  const emergencyBucketBalance = getSubAccountResponse(userBalances, SubAccountType.emergencyBucket)?.balance || 0;
  const taxBucketNavPath = taxBucket?.active ? VIEWS_MAP.TAX_BUCKET_DASHBOARD : VIEWS_MAP.TAX_BUCKET_PERSONALIZATION;
  const { t } = useTranslation();
  if (isLoading) return (<SimpleWidgetSkeleton />);
  return (
    <div className='w-full h-full px-[18px] py-[16px] flex flex-col justify-between'>
      <div className='flex justify-between'>
        <div>
          <Heading title={t('WIDGET_CHECKING_ACCOUNT.TITLE')} className='font-metricRSemibold text-sm text-bw08 leading-5 mb-[2px]' testId={`${testIdPrefix}_title`} />
          <BalanceView
            balance={accountBalance}
            testId={`${testIdPrefix}_balance`}
            currencyClassName='font-metricRMedium text-[16px] text-bw08 leading-6 relative top-[-8px]'
            balanceClassName='font-metricR text-[28px] text-bw08 leading-[42px]'
          />
        </div>
        <div><IconAvailableBalance /></div>
      </div>
      <div>
        {
          showTaxBucketBalance() && (
            <BucketBalance
              title={t('WIDGET_CHECKING_ACCOUNT.TAX_BUCKET')}
              balance={taxBucketBalance}
              testId='taxBucketBalance'
              linkUrl={`/${taxBucketNavPath}`}
              showSeparator
              mixpanelEventName='Act on Manage Tax Bucket'
            />
          )
        }
        {
          showEmergencyBucketBalance() && (
            <BucketBalance
              title={t('WIDGET_CHECKING_ACCOUNT.EMERGENCY_BUCKET')}
              balance={emergencyBucketBalance}
              testId='emergencyBucketBalance'
              linkUrl={`/${VIEWS_MAP.EMERGENCY_BUCKET_DASHBOARD}`}
              mixpanelEventName='Act on Emergency Tax Bucket'
            />
          )
        }
        <div className='flex'>
          <Heading title={t('WIDGET_CHECKING_ACCOUNT.TOTAL_BALANCE')} className='font-metricR text-sm text-bw08 leading-6 mr-[5px]' />
          <BalanceView balance={totalBalance} testId={`${testIdPrefix}_total-balance`} />
        </div>
      </div>
    </div>
  );
};
